import { useState, useCallback } from 'react';
import { useFlashFlag } from '@/hooks/useFlashFlag';
import { cn } from '@/lib/utils';
import { ArrowLeft, Copy, Link, Clock, Merge } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import {
  updateFinanceContactRequest,
  ContactKind,
  ContactType,
  OrganizationType,
  DonorInfoRequestChannel,
  DonorSourceCategory,
  donorSourceCategoryLabel,
  RecipientOfficeCategory,
  recipientOfficeCategoryLabel,
  missingFieldsLabel,
} from '@signalvote/shared';
import { DonorInfoChannelPicker } from '@/components/finance/shared';
import { MergeContactPanel } from './MergeContactPanel';
import type { UpdateFinanceContactRequest } from '@signalvote/shared';
import { useCampaignStore } from '@/stores';
import {
  useFinanceContact,
  useUpdateFinanceContact,
  useLinkContactToVoter,
  useMarkFollowUpSent,
} from '@/hooks/queries/useFinanceContacts';
import { useFinanceSettings, useComplianceProfile } from '@/hooks/queries/useFinanceSettings';
import { useOrganizationTypeOptions } from '@/hooks/useOrganizationTypeOptions';
import { AuditLogPanel } from '../audit';
import {
  AddressAutocomplete,
  canonicalAddressRequestFields,
  type CanonicalAddressValue,
} from '@/components/finance/shared/classification';
import { trafficStatusToCss } from './trafficUtils';
import type { TrafficStatus } from './trafficUtils';
import { OrganizationTypeConflictAlert } from './OrganizationTypeConflictAlert';
import {
  EMPTY_EMPLOYER_ADDRESS,
  EmployerAddressFields,
  employerAddressFrom,
  employerAddressRequest,
} from './EmployerAddressFields';
import { readySettings } from '@/lib/financeSettingsRead';
import { readyProfile } from '@/lib/complianceProfileRead';

// ADR-057 (#344) — the committee OrganizationTypes: an org contact carrying one of these IS a
// registered political committee (the derived "is a committee?" signal that replaced the retired
// federal-only committee-kind field). Drives whether the federal committee-id field is shown.
const COMMITTEE_ORG_TYPES = new Set<OrganizationType | ''>([
  OrganizationType.Pac,
  OrganizationType.Party,
  OrganizationType.CandidateCommittee,
  OrganizationType.OtherCommittee,
]);

// Donor-facing phrase for the window an employer/occupation threshold meters over.
const TRACKING_PERIOD_PHRASE: Partial<Record<string, string>> = {
  perElection: 'per election',
  per4YearCycle: 'over the four-year election cycle',
  annual: 'in a calendar year',
  perCycle: 'over the election cycle',
};

interface ContactDetailProps {
  contactId: string;
  onBack: () => void;
}

export function ContactDetail({ contactId, onBack }: ContactDetailProps) {
  const canDo = useCampaignStore((s) => s.canDo('Finance'));
  const canView = useCampaignStore((s) => s.canView('Finance'));
  // Merge is Finance:Manage on the server, so gate the affordance on the same tier — offering a
  // button that 403s is its own kind of confusing.
  const canManage = useCampaignStore((s) => s.canManage('Finance'));
  const contactRead = useFinanceContact(contactId);
  const contact = contactRead.status === 'ready' ? contactRead.data : null;
  const settings = readySettings(useFinanceSettings());
  const complianceProfile = readyProfile(useComplianceProfile());
  const { mutateAsync: updateContact } = useUpdateFinanceContact();
  const { mutateAsync: linkVoter } = useLinkContactToVoter();
  const { mutateAsync: markFollowUp, isPending: followUpPending } = useMarkFollowUpSent();

  const [editing, setEditing] = useState(false);
  // How the follow-up went out. No default — the treasurer records how they actually asked.
  const [followUpChannel, setFollowUpChannel] = useState<DonorInfoRequestChannel | null>(null);
  const [editContactKind, setEditContactKind] = useState<ContactKind>(ContactKind.Individual);
  const [editFirstName, setEditFirstName] = useState('');
  const [editMiddleName, setEditMiddleName] = useState('');
  const [editLastName, setEditLastName] = useState('');
  const [editNameSuffix, setEditNameSuffix] = useState('');
  const [editOrganizationName, setEditOrganizationName] = useState('');
  // GitHub #63 — FEC committee id for a PAC / party-committee donor (organization contacts).
  const [editCommitteeFecId, setEditCommitteeFecId] = useState('');
  // Multicandidate qualification (GitHub #706) — the date the contributing committee attained
  // the status. Held as a string: the date input speaks `yyyy-MM-dd`, which is also the wire
  // shape, so nothing needs parsing on either side.
  const [editMulticandidateDate, setEditMulticandidateDate] = useState('');
  // ADR-057 — organization SUBTYPE ('' = unspecified). Jurisdiction-neutral (shown for every org
  // contact). It is the single "is a committee?" signal: a committee subtype (PAC / party /
  // candidate committee / other committee) is what reveals the federal committee-id field below.
  const [editOrganizationType, setEditOrganizationType] = useState<OrganizationType | ''>('');
  const [editSourceCategory, setEditSourceCategory] = useState<DonorSourceCategory | ''>('');
  const [editRecipientOffice, setEditRecipientOffice] = useState<RecipientOfficeCategory | ''>('');
  const [editAddress, setEditAddress] = useState('');
  const [editAddressLine2, setEditAddressLine2] = useState('');
  const [editCity, setEditCity] = useState('');
  const [editState, setEditState] = useState('');
  const [editZip, setEditZip] = useState('');
  // Structured Mapbox pick captured during this edit; null unless the treasurer
  // picked a suggestion (and cleared on any hand-edit). Feeds the canonical
  // request fields — never the filing address.
  const [editCanonical, setEditCanonical] = useState<CanonicalAddressValue | null>(null);
  const [editEmail, setEditEmail] = useState('');
  const [editPhone, setEditPhone] = useState('');
  const [editEmployer, setEditEmployer] = useState('');
  const [editOccupation, setEditOccupation] = useState('');
  const [editEmploymentLocation, setEditEmploymentLocation] = useState('');
  const [editEmployerAddress, setEditEmployerAddress] = useState(EMPTY_EMPLOYER_ADDRESS);
  const [voterIdInput, setVoterIdInput] = useState('');
  const [showVoterLink, setShowVoterLink] = useState(false);
  const [copied, flashCopied] = useFlashFlag(2000);
  const [error, setError] = useState<string | null>(null);
  const [showAuditPanel, setShowAuditPanel] = useState(false);
  const [showMerge, setShowMerge] = useState(false);

  const committeeName = settings?.committeeName ?? 'the committee';

  // Jurisdiction-aware donor-disclosure copy. Everything below is derived from
  // the compliance profile — no state names or dollar thresholds are hardcoded.
  // When stateDisplayName is null the jurisdiction is federal; when the
  // threshold is null we degrade gracefully rather than printing "$null".
  const stateName = complianceProfile?.stateDisplayName ?? null;
  const itemizationThreshold = complianceProfile?.itemizationThreshold ?? null;
  const employerOccupationThreshold = complianceProfile?.employerOccupationThreshold ?? null;
  const thresholdMoney =
    itemizationThreshold != null ? `$${itemizationThreshold.toLocaleString()}` : null;
  // "Alabama law" / "Federal law" — reads naturally before "requires committees".
  const lawSubject = stateName ? `${stateName} law` : 'Federal law';
  // "Alabama's" / "the federal" — possessive before the threshold figure.
  const thresholdOwner = stateName ? `${stateName}'s` : 'the federal';
  // "Alabama filing" / "federal filing".
  const filingLabel = stateName ? `${stateName} filing` : 'federal filing';

  // #479 (#477 cluster A — W1) — the org-subtype picker is gated on the jurisdiction's declared
  // supported set (#478) instead of the full 7-value enum, sharing the derivation with the triage
  // classify picker (W2). Passing the current edit value keeps an already-persisted subtype the
  // jurisdiction no longer models selectable (show-but-don't-offer) so a re-file never drops it.
  const organizationTypeOptions = useOrganizationTypeOptions(complianceProfile, editOrganizationType);
  // Offered only where the ruleset declares banned-source categories (New Jersey); elsewhere the
  // field is neither shown nor sent, so a save there can never touch a recorded category.
  const sourceCategories = complianceProfile?.sourceCategories ?? [];
  const employerAddressRequired = complianceProfile?.employerAddressRequired ?? false;
  // A category recorded before the ruleset stopped declaring it. Shown so the treasurer can clear
  // or change it, and never re-sent unchanged, since the server refuses an undeclared category.
  const staleSourceCategory =
    contact?.sourceCategory && !sourceCategories.includes(contact.sourceCategory)
      ? contact.sourceCategory
      : null;
  // A committee's office type, which New Jersey files as the benefit type of money given to or spent
  // for it. Offered on an organization only where the ruleset declares the categories; elsewhere it
  // is neither shown nor sent. A recorded value no longer offered stays visible so it can be cleared.
  const recipientOfficeCategories = complianceProfile?.recipientOfficeCategories ?? [];
  const staleRecipientOffice =
    contact?.recipientOfficeCategory && !recipientOfficeCategories.includes(contact.recipientOfficeCategory)
      ? contact.recipientOfficeCategory
      : null;

  // The gate for every federal committee-only field: the committee FEC id and the multicandidate
  // qualification both exist because federal law models them, and both are meaningless without a
  // committee subtype to hang on. Derived once rather than re-spelled at each site — the render
  // gates and the save payload disagreeing is how a field gets collected and then silently
  // dropped, or shown and then never sent.
  const showsFederalCommitteeFields =
    !stateName && COMMITTEE_ORG_TYPES.has(editOrganizationType);

  const handleCopyTemplate = useCallback(() => {
    if (!contact) return;
    // A declared employer/occupation threshold (Maryland: $500 over the cycle) is the trigger the
    // donor needs to hear; the itemization threshold beside it may be $0.
    const giveClause = employerOccupationThreshold
      ? [`a total of $${employerOccupationThreshold.amount.toLocaleString()} or more`, TRACKING_PERIOD_PHRASE[employerOccupationThreshold.window]].filter(Boolean).join(' ')
      : thresholdMoney ? `more than ${thresholdMoney}` : 'above the reporting threshold';
    const template = `Dear ${contact.name}, ${lawSubject} requires committees to report the employer and occupation of contributors who give ${giveClause}. Please reply with your current employer and occupation. Thank you, ${committeeName}`;
    navigator.clipboard.writeText(template).then(() => {
      flashCopied();
    });
  }, [contact, committeeName, lawSubject, thresholdMoney, employerOccupationThreshold, flashCopied]);

  const handleMarkFollowUp = useCallback(async () => {
    if (!contact || !followUpChannel) return;
    try {
      await markFollowUp({ row: contact, channel: followUpChannel });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to record follow-up');
    }
  }, [contact, followUpChannel, markFollowUp]);

  const handleLinkVoter = useCallback(async () => {
    if (!contact || !voterIdInput.trim()) return;
    try {
      await linkVoter({ row: contact, voterId: voterIdInput.trim() });
      setShowVoterLink(false);
      setVoterIdInput('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to link voter');
    }
  }, [contact, linkVoter, voterIdInput]);

  const handleUnlinkVoter = useCallback(async () => {
    if (!contact) return;
    try {
      await linkVoter({ row: contact, voterId: null });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to unlink voter');
    }
  }, [contact, linkVoter]);

  const startEditing = useCallback(() => {
    if (!contact) return;
    setEditing(true);
    setEditContactKind(contact.contactKind);
    setEditFirstName(contact.firstName ?? '');
    setEditMiddleName(contact.middleName ?? '');
    setEditLastName(contact.lastName ?? '');
    setEditNameSuffix(contact.nameSuffix ?? '');
    setEditOrganizationName(contact.organizationName ?? '');
    setEditCommitteeFecId(contact.committeeFecId ?? '');
    setEditMulticandidateDate(contact.multicandidateQualifiedDate ?? '');
    setEditOrganizationType(contact.organizationType ?? '');
    setEditSourceCategory(contact.sourceCategory ?? '');
    setEditRecipientOffice(contact.recipientOfficeCategory ?? '');
    setEditAddress(contact.address ?? '');
    setEditAddressLine2(contact.addressLine2 ?? '');
    setEditCity(contact.city ?? '');
    setEditState(contact.state ?? '');
    setEditZip(contact.zipCode ?? '');
    // The stored address is not a fresh Mapbox pick — start with no canonical.
    setEditCanonical(null);
    setEditEmail(contact.email ?? '');
    setEditPhone(contact.phone ?? '');
    setEditEmployer(contact.employer ?? '');
    setEditOccupation(contact.occupation ?? '');
    setEditEmploymentLocation(contact.employmentLocation ?? '');
    setEditEmployerAddress(employerAddressFrom(contact));
  }, [contact]);

  const handleSaveEdit = useCallback(async () => {
    if (!contact) return;
    const isIndividual = editContactKind === ContactKind.Individual;
    // ADR-057 (#344) — "is a committee?" is DERIVED from the organization subtype (the four
    // committee OrganizationTypes), replacing the retired federal-only committee-kind field.
    // A committee subtype is what makes the federal committee-id field meaningful.
    const isCommittee = !isIndividual && COMMITTEE_ORG_TYPES.has(editOrganizationType);
    const data: UpdateFinanceContactRequest = updateFinanceContactRequest({
      contactKind: editContactKind,
      firstName: isIndividual ? editFirstName.trim() : null,
      middleName: isIndividual ? (editMiddleName.trim() || null) : null,
      lastName: isIndividual ? editLastName.trim() : null,
      nameSuffix: isIndividual ? (editNameSuffix.trim() || null) : null,
      organizationName: isIndividual ? null : editOrganizationName.trim(),
      // GitHub #63 — committee id is a federal-only field on organization committees. On a
      // federal campaign send the value ('' clears it) only while a committee SUBTYPE is set;
      // for an individual, or a non-committee org, send '' so a mis-entered id can't linger with
      // no committee to attach to. On a state campaign (cluster hidden) omit it entirely
      // (null ⇒ leave unchanged).
      committeeFecId: stateName
        ? null
        : (isCommittee ? (editCommitteeFecId.trim() || '') : ''),
      // ADR-057 — organization subtype. Jurisdiction-NEUTRAL (the picker shows on every campaign),
      // so unlike committee kind there is no stateName gate: always SET the selected subtype for an
      // org, else explicitly CLEAR it (covers an individual and an org left "Unspecified"). The
      // picker is always visible when editing, so this can never wipe an unseen value.
      ...(!isIndividual && editOrganizationType
        ? { organizationType: editOrganizationType }
        : { clearOrganizationType: true }),
      ...(editSourceCategory === (contact.sourceCategory ?? '')
        ? {}
        : editSourceCategory
          ? { sourceCategory: editSourceCategory }
          : { clearSourceCategory: true }),
      ...(editRecipientOffice === (contact.recipientOfficeCategory ?? '')
        ? {}
        : editRecipientOffice
          ? { recipientOfficeCategory: editRecipientOffice }
          : { clearRecipientOfficeCategory: true }),
      // Multicandidate qualification (#706) — federal-only and committee-only, exactly where the
      // field is offered. Emptying the date is the treasurer withdrawing the claim, so it must
      // reach the server as an explicit CLEAR: an omitted field means "leave it alone", and a
      // blanked input that sent nothing would quietly keep the higher ceiling in force. Outside
      // that cluster send nothing at all (undefined ⇒ unchanged) rather than a clear, so an edit
      // from a state campaign — where the field is never shown — can't wipe a federal record.
      ...(showsFederalCommitteeFields
        ? (editMulticandidateDate
            ? { multicandidateQualifiedDate: editMulticandidateDate }
            : { clearMulticandidateQualification: true })
        : {}),
      address: editAddress.trim() || null,
      addressLine2: editAddressLine2.trim() || null,
      city: editCity.trim() || null,
      state: editState.trim() || null,
      zipCode: editZip.trim() || null,
      email: editEmail.trim() || null,
      phone: editPhone.trim() || null,
      employer: editEmployer.trim() || null,
      occupation: editOccupation.trim() || null,
      employmentLocation: editEmploymentLocation.trim() || null,
      ...employerAddressRequest(editEmployerAddress),
      // Canonical parallel — sent only when the treasurer picked a Mapbox
      // suggestion this edit; never overwrites the filing address above.
      ...canonicalAddressRequestFields(
        editCanonical
          ? {
              canonicalAddressLine1: editCanonical.line1,
              canonicalAddressLine2: editCanonical.line2 || null,
              canonicalCity: editCanonical.city,
              canonicalState: editCanonical.state,
              canonicalZip: editCanonical.zip,
            }
          : {},
      ),
      concurrencyVersion: contact.concurrencyVersion,
    });
    try {
      await updateContact({ row: contact, data });
      // Clear any previous failure. Without this a rejected save leaves its banner on screen
      // through the successful retry, so the treasurer reads "this was refused" directly above
      // the value that was just accepted.
      setError(null);
      setEditing(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update contact');
    }
  }, [contact, editContactKind, editFirstName, editMiddleName, editLastName, editNameSuffix, editOrganizationName, editCommitteeFecId, editOrganizationType, editSourceCategory, editRecipientOffice, editMulticandidateDate, showsFederalCommitteeFields, stateName, editAddress, editAddressLine2, editCity, editState, editZip, editEmail, editPhone, editEmployer, editOccupation, editEmploymentLocation, editEmployerAddress, editCanonical, updateContact]);

  if (contactRead.status === 'failed' || contactRead.status === 'offline') {
    return (
      <div className="p-6">
        <p role="alert" className="text-destructive">
          {contactRead.status === 'failed'
            ? "Couldn't load this contact. Try again shortly."
            : "You're offline, so this contact can't load."}
        </p>
        <button type="button" onClick={onBack} aria-label="Back to contacts" className="mt-2 text-sm text-primary underline">
          Back
        </button>
      </div>
    );
  }

  if (contactRead.status !== 'ready') {
    return (
      <div className="p-6">
        <p className="text-muted-foreground">Loading contact...</p>
      </div>
    );
  }

  if (!contact) {
    return (
      <div className="p-6">
        <p className="text-muted-foreground">Contact not found.</p>
        <button type="button" onClick={onBack} aria-label="Back to contacts" className="mt-2 text-sm text-primary underline">
          Back
        </button>
      </div>
    );
  }

  const isDonor = contact.contactType === ContactType.Donor;
  const count = isDonor ? contact.contributionCount : contact.paymentCount;
  const countLabel = isDonor ? 'contributions' : 'payments';
  // Backend-computed, ruleset-aware set of itemization fields this donor is
  // missing for filing (empty below the threshold OR when complete). Alabama
  // never lists employer/occupation — for an over-$100 AL donor it lists the
  // field AL actually requires: address.
  const missingFields = isDonor ? (contact.missingItemizedFields ?? []) : [];
  const stateZip = [contact.state, contact.zipCode].filter(Boolean).join(' ');
  const mailingAddressLine = [contact.address, contact.addressLine2, contact.city, stateZip]
    .filter(Boolean)
    .join(', ');
  const employerAddressLine = [contact.employerStreet, contact.employerCity, contact.employerState, contact.employerZip]
    .filter(Boolean)
    .join(', ');
  // The follow-up flow requests employer/occupation FROM the donor, so it only
  // makes sense when one of those is what's missing; an address/name gap is fixed
  // on the contact record via Edit Contact.
  const needsEmployerOccupation =
    missingFields.includes('Employer') ||
    missingFields.includes('Occupation') ||
    missingFields.includes('VagueOccupation');
  const dotColor = trafficStatusToCss(contact.trafficStatus as TrafficStatus);

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button type="button" onClick={onBack} aria-label="Back to contacts">
            <ArrowLeft className="h-5 w-5" />
          </button>
          <span
            data-testid="detail-traffic-dot"
            className={cn('h-3 w-3 shrink-0 rounded-full', dotColor)}
          />
          <h2 className="text-lg font-semibold">{contact.name}</h2>
          <Badge variant="secondary">{isDonor ? 'Donor' : 'Payee'}</Badge>
        </div>
        {canView && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowAuditPanel(true)}
            title="View change history"
          >
            <Clock className="h-4 w-4" />
          </Button>
        )}
      </div>

      {/* Contact-merge OrganizationType conflict fix-it alert (#350) — renders only when this
          contact has an unresolved conflict and the user can manage finance. Resolving/dismissing
          here also clears the filing preflight backstop (shared audit-derived source). */}
      <OrganizationTypeConflictAlert contactId={contact.id} />

      {/* Merge — the contacts list already tells treasurers they can merge "from the contact",
          so this is where they come looking. Duplicates that the automatic check never flagged
          (a nickname like Will/William shares no email) can only be resolved here. */}
      {canManage && !editing && (
        showMerge ? (
          <MergeContactPanel
            contact={contact}
            onClose={() => setShowMerge(false)}
            onMerged={(survivorContactId) => {
              setShowMerge(false);
              // Leaving the merged-away record open would show a 404; go to the one that survived.
              if (survivorContactId !== contact.id) onBack();
            }}
          />
        ) : (
          <Button
            variant="outline"
            size="sm"
            data-testid="contact-merge-open"
            onClick={() => setShowMerge(true)}
          >
            <Merge className="mr-2 h-4 w-4" />
            Merge with another record
          </Button>
        )
      )}

      {/* Aggregates */}
      <div className="space-y-1">
        <div className="flex gap-6 text-sm">
          <div>
            <span className="font-medium">${contact.calendarYearTotal.toLocaleString()}</span>
            <span className="text-muted-foreground ml-1">calendar year total</span>
          </div>
          <div>
            <span className="font-medium">{count}</span>
            <span className="text-muted-foreground ml-1">{countLabel}</span>
          </div>
        </div>

        {/* Itemization verdict — displays the profile threshold vs this donor's
            calendar-year total. Only rendered when the jurisdiction publishes a
            threshold (federal or a state); the ruleset is the source of truth. */}
        {isDonor && thresholdMoney != null && itemizationThreshold != null && (
          // A $0 threshold (e.g. Louisiana, Maryland) itemizes every receipt, so "over/under $0" says nothing.
          itemizationThreshold === 0 ? (
            <p className="text-sm text-muted-foreground">
              {stateName ?? 'This jurisdiction'} itemizes every contribution — each one appears in
              full in your {filingLabel}.
            </p>
          ) : contact.calendarYearTotal > itemizationThreshold ? (
            <p className="text-sm text-amber-700">
              Over {thresholdOwner} {thresholdMoney} calendar-year itemization threshold — these
              contributions will be itemized (name, address, employer, occupation) in your {filingLabel}.
            </p>
          ) : (
            <p className="text-sm text-muted-foreground">
              Under {thresholdOwner} {thresholdMoney} itemization threshold — not itemized yet.
            </p>
          )
        )}
      </div>

      {/* Contact Info */}
      <div className="space-y-2 text-sm">
        {contact.address && (
          <div><span className="text-muted-foreground">Address:</span> {mailingAddressLine}</div>
        )}
        {contact.email && (
          <div><span className="text-muted-foreground">Email:</span> {contact.email}</div>
        )}
        {contact.phone && (
          <div><span className="text-muted-foreground">Phone:</span> {contact.phone}</div>
        )}
        {contact.employer && (
          <div><span className="text-muted-foreground">Employer:</span> {contact.employer}</div>
        )}
        {contact.occupation && (
          <div><span className="text-muted-foreground">Occupation:</span> {contact.occupation}</div>
        )}
        {contact.employmentLocation && (
          <div><span className="text-muted-foreground">Employment location:</span> {contact.employmentLocation}</div>
        )}
        {employerAddressRequired && employerAddressLine && (
          <div><span className="text-muted-foreground">Employer address:</span> {employerAddressLine}</div>
        )}
        {/* GitHub #706 — a recorded multicandidate qualification. This one field decides which of
            two federal ceilings the committee's gifts are priced against, so it is shown rather
            than buried in the edit form. Absence is not rendered: every committee starts on the
            lower ceiling, so "nothing here" is the normal case, not a gap to nag about. */}
        {contact.multicandidateQualifiedDate && (
          <div>
            <span className="text-muted-foreground">Multicandidate committee since:</span>{' '}
            {contact.multicandidateQualifiedDate}
          </div>
        )}
      </div>

      {/* Missing-info callout — shown ONLY for an itemized (over-threshold) donor
          missing a field their jurisdiction actually requires. Names the specific
          fields (AL: address; federal: employer/occupation/address) instead of
          hardcoding "employer and occupation". */}
      {missingFields.length > 0 && (
        <div className="rounded-md border border-amber-300 bg-amber-50 p-4 space-y-3">
          <p className="text-sm font-medium text-amber-800">
            Missing {missingFieldsLabel(missingFields)} — required to itemize this ${contact.calendarYearTotal.toLocaleString()}{' '}
            donor in your {filingLabel}.
          </p>

          {needsEmployerOccupation ? (
            // #427 — "Mark Follow-Up Sent" records the per-gift best-efforts signal (a
            // DonorInfoRequested audit row) on this donor's currently-recorded gifts; it no longer
            // sets a standing contact-level date, so there is no "sent on / days remaining" window.
            // The contact still shows the gap (a future gift from the same donor must still nag).
            canDo && (
              <div className="space-y-2">
                <DonorInfoChannelPicker
                  value={followUpChannel}
                  onChange={setFollowUpChannel}
                  disabled={followUpPending}
                  id="follow-up-channel"
                  className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-amber-800"
                />
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={handleMarkFollowUp}
                  disabled={followUpPending || !followUpChannel}
                  className="rounded-md bg-primary px-3 py-1 text-sm text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
                >
                  Mark Follow-Up Sent
                </button>
                <button
                  type="button"
                  onClick={handleCopyTemplate}
                  className="flex items-center gap-1 rounded-md border px-3 py-1 text-sm hover:bg-accent"
                >
                  <Copy className="h-3 w-3" />
                  {copied ? 'Copied!' : 'Copy Follow-Up Template'}
                </button>
              </div>
              </div>
            )
          ) : (
            // Address / name gaps are fixed on the contact record, not by asking
            // the donor — point the treasurer at Edit Contact below.
            <p className="text-sm text-amber-700">
              Add the missing {missingFieldsLabel(missingFields)} with Edit Contact below.
            </p>
          )}
        </div>
      )}

      {/* Voter cross-reference */}
      <div className="space-y-2">
        {contact.voterId ? (
          <div className="flex items-center gap-2 text-sm">
            <Link className="h-4 w-4 text-muted-foreground" />
            <span>Linked to voter: {contact.voterId}</span>
            {canDo && (
              <button
                type="button"
                onClick={handleUnlinkVoter}
                className="text-xs text-destructive underline"
              >
                Unlink
              </button>
            )}
          </div>
        ) : canDo && (
          <div>
            {showVoterLink ? (
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={voterIdInput}
                  onChange={(e) => setVoterIdInput(e.target.value)}
                  placeholder="Enter voter ID"
                  className="rounded-md border px-3 py-1 text-sm"
                />
                <button
                  type="button"
                  onClick={handleLinkVoter}
                  className="rounded-md bg-primary px-3 py-1 text-sm text-primary-foreground"
                >
                  Link
                </button>
                <button
                  type="button"
                  onClick={() => setShowVoterLink(false)}
                  className="text-sm text-muted-foreground underline"
                >
                  Cancel
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setShowVoterLink(true)}
                className="flex items-center gap-1 text-sm text-primary underline"
              >
                <Link className="h-3 w-3" /> Link to Voter
              </button>
            )}
          </div>
        )}
      </div>

      {/* Edit section */}
      {canDo && !editing && (
        <button
          type="button"
          onClick={startEditing}
          className="text-sm text-primary underline"
        >
          Edit Contact
        </button>
      )}

      {editing && (
        <div className="space-y-3 rounded-md border p-4">
          <h3 className="text-sm font-medium">Edit Contact</h3>
          <div>
            <label className="block text-xs text-muted-foreground">Kind</label>
            <select
              value={editContactKind}
              onChange={(e) => setEditContactKind(e.target.value as ContactKind)}
              className="w-full rounded-md border px-2 py-1 text-sm"
            >
              <option value={ContactKind.Individual}>Individual</option>
              <option value={ContactKind.Organization}>Organization</option>
            </select>
          </div>
          {editContactKind === ContactKind.Individual ? (
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-xs text-muted-foreground">First Name</label>
                <input type="text" value={editFirstName} onChange={(e) => setEditFirstName(e.target.value)} className="w-full rounded-md border px-2 py-1 text-sm" />
              </div>
              <div>
                <label className="block text-xs text-muted-foreground">Middle</label>
                <input type="text" value={editMiddleName} onChange={(e) => setEditMiddleName(e.target.value)} className="w-full rounded-md border px-2 py-1 text-sm" />
              </div>
              <div>
                <label className="block text-xs text-muted-foreground">Last Name</label>
                <input type="text" value={editLastName} onChange={(e) => setEditLastName(e.target.value)} className="w-full rounded-md border px-2 py-1 text-sm" />
              </div>
              <div>
                <label className="block text-xs text-muted-foreground">Suffix</label>
                <input type="text" value={editNameSuffix} onChange={(e) => setEditNameSuffix(e.target.value)} className="w-full rounded-md border px-2 py-1 text-sm" />
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              <div>
                <label className="block text-xs text-muted-foreground">Organization Name</label>
                <input type="text" value={editOrganizationName} onChange={(e) => setEditOrganizationName(e.target.value)} className="w-full rounded-md border px-2 py-1 text-sm" />
              </div>
              {/* ADR-057 / #479 — the organization SUBTYPE. Shown for every org contact when editing,
                  but the OFFERED set is now gated on the jurisdiction's declared supported subtypes
                  (#478 via useOrganizationTypeOptions), not the full 7-value enum: an AL treasurer no
                  longer sees "Labor union" / "Party committee" (values AL doesn't model). "Is a
                  committee?" is derived from it. "Unspecified" leaves it null. */}
              <div>
                <label htmlFor="contact-detail-organization-type" className="block text-xs text-muted-foreground">Organization type</label>
                <select
                  id="contact-detail-organization-type"
                  data-testid="contact-detail-organization-type"
                  value={editOrganizationType}
                  onChange={(e) => setEditOrganizationType(e.target.value as OrganizationType | '')}
                  className="w-full rounded-md border px-2 py-1 text-sm"
                >
                  <option value="">Unspecified</option>
                  {organizationTypeOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                {/* #479 (#477 decision 2) — a value persisted before a ruleset switch that this
                    jurisdiction no longer models stays selectable above and is surfaced here for
                    re-review, so nothing is silently dropped or reclassified. */}
                {organizationTypeOptions.some((o) => o.unsupported && o.value === editOrganizationType) && (
                  <p className="mt-1 text-xs text-amber-700">
                    This type isn't used in your jurisdiction's filings — kept so it isn't lost. Re-classify if it no longer fits.
                  </p>
                )}
              </div>
              {/* GitHub #63 / ADR-057 — the committee FEC ID is a federal-only Schedule A/B field.
                  Show it only for federal campaigns (stateName === null); state jurisdictions like
                  Alabama carry no such field. "Is a committee?" is now derived from the
                  organization SUBTYPE above (the four committee OrganizationTypes) — picking a
                  committee subtype is what reveals the id and makes it emit on a committee-to-
                  committee disbursement. Leaving the id blank is allowed (alert-don't-block,
                  #87/ADR-030) — a soft nudge points the treasurer at it. */}
              {showsFederalCommitteeFields && (
                <div>
                  <label htmlFor="contact-detail-committee-id" className="block text-xs text-muted-foreground">Committee ID</label>
                  <input
                    id="contact-detail-committee-id"
                    data-testid="contact-detail-committee-id"
                    type="text"
                    value={editCommitteeFecId}
                    onChange={(e) => setEditCommitteeFecId(e.target.value)}
                    placeholder="C00123456"
                    className="w-full rounded-md border px-2 py-1 text-sm"
                  />
                  {!editCommitteeFecId.trim() && (
                    <p className="mt-1 text-xs text-muted-foreground">
                      Committee ID missing — add it now, or it'll be flagged at filing.
                    </p>
                  )}
                </div>
              )}
              {/* GitHub #706 — multicandidate qualification. Federal law gives a contributing
                  committee two per-election ceilings and picks between them on the committee's OWN
                  standing: a flat, never-indexed $5,000 once it has qualified as multicandidate
                  (52 U.S.C. § 30116(a)(4)), and the same indexed ceiling an individual gets until
                  then. Nothing on an incoming check reveals which, so the treasurer records it
                  here from the committee's Form 1M, its FEC profile, or its own declaration.
                  Shown on the same federal + committee-subtype gate as the id above: state
                  jurisdictions model one ceiling per donor type, so asking there would collect a
                  fact that changes nothing. Left blank the committee gets the LOWER ceiling —
                  the fail-safe, since the app cannot verify someone else's FEC history. */}
              {showsFederalCommitteeFields && (
                <div>
                  <label htmlFor="contact-detail-multicandidate-date" className="block text-xs text-muted-foreground">
                    Qualified as multicandidate committee
                  </label>
                  <input
                    id="contact-detail-multicandidate-date"
                    data-testid="contact-detail-multicandidate-date"
                    type="date"
                    value={editMulticandidateDate}
                    onChange={(e) => setEditMulticandidateDate(e.target.value)}
                    className="w-full rounded-md border px-2 py-1 text-sm"
                  />
                  {!editMulticandidateDate && (
                    <p className="mt-1 text-xs text-muted-foreground">
                      Leave blank unless you've confirmed it. Until then this committee is held to
                      the same limit as an individual donor.
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
          {editContactKind === ContactKind.Organization
            && (recipientOfficeCategories.length > 0 || staleRecipientOffice) && (
            <div>
              <label htmlFor="contact-detail-recipient-office-category" className="block text-xs text-muted-foreground">
                Office its candidate seeks
              </label>
              <select
                id="contact-detail-recipient-office-category"
                data-testid="contact-detail-recipient-office-category"
                value={editRecipientOffice}
                onChange={(e) => setEditRecipientOffice(e.target.value as RecipientOfficeCategory | '')}
                className="w-full rounded-md border px-2 py-1 text-sm"
              >
                <option value="">Not set</option>
                {staleRecipientOffice && (
                  <option value={staleRecipientOffice}>
                    {recipientOfficeCategoryLabel(staleRecipientOffice)} (no longer used here)
                  </option>
                )}
                {recipientOfficeCategories.map((category) => (
                  <option key={category} value={category}>{recipientOfficeCategoryLabel(category)}</option>
                ))}
              </select>
              <p className="mt-1 text-xs text-muted-foreground">
                Set this on any committee you give to or spend for. {stateName ?? 'The state'} files it with that money.
              </p>
            </div>
          )}
          {(sourceCategories.length > 0 || staleSourceCategory) && (
            <div>
              <label htmlFor="contact-detail-source-category" className="block text-xs text-muted-foreground">Banned source</label>
              <select
                id="contact-detail-source-category"
                data-testid="contact-detail-source-category"
                value={editSourceCategory}
                onChange={(e) => setEditSourceCategory(e.target.value as DonorSourceCategory | '')}
                className="w-full rounded-md border px-2 py-1 text-sm"
              >
                <option value="">None</option>
                {staleSourceCategory && (
                  <option value={staleSourceCategory}>
                    {donorSourceCategoryLabel(staleSourceCategory)} (no longer a banned source here)
                  </option>
                )}
                {sourceCategories.map((category) => (
                  <option key={category} value={category}>{donorSourceCategoryLabel(category)}</option>
                ))}
              </select>
            </div>
          )}
          {/* Address block — Mapbox autocomplete. Picking a suggestion fills the
              filing fields AND captures the structured pick as the canonical
              address (feeds dedup tier-3); a hand-typed / edited address clears it. */}
          <AddressAutocomplete
            idPrefix="contact-detail"
            value={{
              address: editAddress,
              addressLine2: editAddressLine2,
              city: editCity,
              state: editState,
              zip: editZip,
            }}
            onChange={(next) => {
              setEditAddress(next.address);
              setEditAddressLine2(next.addressLine2);
              setEditCity(next.city);
              setEditState(next.state);
              setEditZip(next.zip);
            }}
            onCanonicalChange={setEditCanonical}
          />
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-xs text-muted-foreground">Email</label>
              <input type="text" value={editEmail} onChange={(e) => setEditEmail(e.target.value)} className="w-full rounded-md border px-2 py-1 text-sm" />
            </div>
            <div>
              <label className="block text-xs text-muted-foreground">Phone</label>
              <input type="text" value={editPhone} onChange={(e) => setEditPhone(e.target.value)} className="w-full rounded-md border px-2 py-1 text-sm" />
            </div>
            <div>
              <label className="block text-xs text-muted-foreground">Employer</label>
              <input type="text" value={editEmployer} onChange={(e) => setEditEmployer(e.target.value)} className="w-full rounded-md border px-2 py-1 text-sm" />
            </div>
            <div>
              <label className="block text-xs text-muted-foreground">Occupation</label>
              <input type="text" value={editOccupation} onChange={(e) => setEditOccupation(e.target.value)} className="w-full rounded-md border px-2 py-1 text-sm" />
            </div>
            <div className="col-span-2">
              <label htmlFor="contact-detail-employment-location" className="block text-xs text-muted-foreground">Employment location</label>
              <input id="contact-detail-employment-location" type="text" value={editEmploymentLocation} onChange={(e) => setEditEmploymentLocation(e.target.value)} placeholder="City, State" className="w-full rounded-md border px-2 py-1 text-sm" />
            </div>
            {employerAddressRequired && (
              <EmployerAddressFields
                idPrefix="contact-detail"
                value={editEmployerAddress}
                onChange={setEditEmployerAddress}
                labelClassName="block text-xs text-muted-foreground"
                inputClassName="w-full rounded-md border px-2 py-1 text-sm"
              />
            )}
          </div>
          <div className="flex gap-2">
            <button type="button" onClick={handleSaveEdit} className="rounded-md bg-primary px-3 py-1 text-sm text-primary-foreground">Save</button>
            <button type="button" onClick={() => setEditing(false)} className="text-sm text-muted-foreground underline">Cancel</button>
          </div>
        </div>
      )}

      {/* Error display */}
      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
          {error}
        </div>
      )}

      {/* Audit Log Sheet */}
      <Sheet open={showAuditPanel} onOpenChange={setShowAuditPanel}>
        <SheetContent side="right" className="w-96 p-0">
          <AuditLogPanel
            entityType="Contact"
            entityId={contact.id}
            onClose={() => setShowAuditPanel(false)}
          />
        </SheetContent>
      </Sheet>
    </div>
  );
}
